const toAsciiDigits = value => String(value ?? '')
  .replace(/[۰-۹]/g, digit => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(digit)))
  .replace(/[٠-٩]/g, digit => String('٠١٢٣٤٥٦٧٨٩'.indexOf(digit)))
  .replace(/\D/g, '');

document.querySelectorAll('[data-otp-entry]').forEach(setupOtpEntry);
document.querySelectorAll('[data-resend-button]').forEach(setupResendButton);
document.querySelectorAll('[data-phone-profile-form]').forEach(setupProfileForm);

function setupOtpEntry(entry) {
  const hidden = entry.querySelector('[data-otp-value]');
  const digits = [...entry.querySelectorAll('[data-otp-digit]')];
  if (!hidden || digits.length === 0) return;
  const form = entry.closest('form');
  const submitButton = form?.querySelector('button[type="submit"], input[type="submit"]');
  let submitting = false;
  let initialValueHydrated = false;

  const syncHidden = () => {
    hidden.value = digits.map(input => toAsciiDigits(input.value).slice(0, 1)).join('');
    hidden.dispatchEvent(new Event('change', { bubbles: true }));
  };
  const lockForSubmit = () => {
    submitting = true;
    entry.setAttribute('aria-busy', 'true');
    digits.forEach(input => { input.readOnly = true; });
  };
  const submitWhenComplete = () => {
    if (!initialValueHydrated || submitting || !form || hidden.value.length !== digits.length) return;
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }
    lockForSubmit();
    // requestSubmit preserves validation, the clicked submitter and every
    // existing Razor/AJAX submit handler. A click fallback covers older WebKit.
    queueMicrotask(() => {
      if (typeof form.requestSubmit === 'function') form.requestSubmit(submitButton ?? undefined);
      else submitButton?.click();
      if (submitButton) submitButton.disabled = true;
    });
  };
  const fill = (value, startIndex = 0, autoSubmit = true) => {
    const normalized = toAsciiDigits(value).slice(0, digits.length - startIndex);
    if (!normalized) return;
    normalized.split('').forEach((digit, offset) => { digits[startIndex + offset].value = digit; });
    syncHidden();
    const next = Math.min(startIndex + normalized.length, digits.length - 1);
    digits[next].focus();
    digits[next].select();
    if (autoSubmit) submitWhenComplete();
  };

  // Do not automatically resubmit a server-rendered invalid code after a
  // validation response. Only a fresh user/Paste/OTP-autofill change submits.
  if (hidden.value) fill(hidden.value, 0, false);
  initialValueHydrated = true;
  digits.forEach((input, index) => {
    input.addEventListener('input', () => {
      const normalized = toAsciiDigits(input.value);
      if (normalized.length > 1) {
        fill(normalized, index);
        return;
      }
      input.value = normalized.slice(0, 1);
      syncHidden();
      if (input.value && index < digits.length - 1) {
        digits[index + 1].focus();
        digits[index + 1].select();
      }
      submitWhenComplete();
    });
    input.addEventListener('keydown', event => {
      if (event.key === 'Backspace' && !input.value && index > 0) {
        event.preventDefault();
        digits[index - 1].value = '';
        digits[index - 1].focus();
        syncHidden();
      } else if (event.key === 'ArrowLeft' && index > 0) {
        event.preventDefault();
        digits[index - 1].focus();
      } else if (event.key === 'ArrowRight' && index < digits.length - 1) {
        event.preventDefault();
        digits[index + 1].focus();
      }
    });
    input.addEventListener('paste', event => {
      const value = event.clipboardData?.getData('text') ?? '';
      if (!toAsciiDigits(value)) return;
      event.preventDefault();
      fill(value, index);
    });
    input.addEventListener('focus', () => input.select());
  });
  form?.addEventListener('submit', event => {
    syncHidden();
    if (event.defaultPrevented || submitting || hidden.value.length !== digits.length) return;
    lockForSubmit();
    if (submitButton) submitButton.disabled = true;
  });
  window.addEventListener('pageshow', event => {
    if (!event.persisted) return;
    submitting = false;
    entry.removeAttribute('aria-busy');
    digits.forEach(input => { input.readOnly = false; });
    if (submitButton) submitButton.disabled = false;
  });
  if (entry.hasAttribute('data-auto-focus')) requestAnimationFrame(() => digits[0].focus());
}

function setupResendButton(button) {
  let remaining = Number(button.dataset.waitSeconds || 0);
  if (!Number.isFinite(remaining) || remaining <= 0) return;
  const original = button.textContent.trim();
  button.disabled = true;
  const render = () => {
    if (remaining <= 0) {
      button.disabled = false;
      button.textContent = original;
      return;
    }
    button.textContent = `ارسال دوباره تا ${remaining.toLocaleString('fa-IR')} ثانیه`;
    remaining -= 1;
    window.setTimeout(render, 1000);
  };
  render();
}

function setupProfileForm(form) {
  const input = form.querySelector('[data-profile-username]');
  const status = form.querySelector('[data-profile-username-status]');
  if (!input || !status || !form.dataset.usernameCheckUrl) return;
  let timer = 0;
  let request = null;
  let availability = null;

  input.addEventListener('input', () => {
    const normalized = String(input.value || '').toLowerCase().replace(/[^a-z0-9._-]/g, '').slice(0, 24);
    if (input.value !== normalized) input.value = normalized;
    availability = null;
    status.className = 'auth-field-status';
    status.textContent = normalized.length < 3 ? 'حداقل ۳ کاراکتر انگلیسی' : 'در حال بررسی…';
    clearTimeout(timer);
    request?.abort();
    if (normalized.length >= 3) timer = window.setTimeout(check, 320);
  });

  form.addEventListener('submit', event => {
    if (availability === false) {
      event.preventDefault();
      input.focus();
      status.textContent = 'یک نام کاربری آزاد انتخاب کنید.';
      status.className = 'auth-field-status error';
    }
  });

  if (input.value) check();

  async function check() {
    const value = input.value;
    request = new AbortController();
    try {
      const url = new URL(form.dataset.usernameCheckUrl, location.origin);
      url.searchParams.set('userName', value);
      const response = await fetch(url, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        signal: request.signal
      });
      if (!response.ok) throw new Error('username_check_failed');
      const result = await response.json();
      availability = Boolean(result.available);
      if (result.normalized) input.value = result.normalized;
      status.textContent = result.message;
      status.className = `auth-field-status ${availability ? 'success' : 'error'}`;
    } catch (error) {
      if (error.name === 'AbortError') return;
      availability = null;
      status.textContent = 'هنگام ثبت نهایی بررسی می‌شود.';
      status.className = 'auth-field-status warning';
    }
  }
}
